import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Coffee, Gift, Loader2, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const DONATION_AMOUNTS = [10, 20, 50];

export const DonationModal = ({ isOpen, onClose, afterMeetup = false }) => {
  const { i18n } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState(20);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const getAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const handleDonate = async () => {
    const amount = getAmount();
    if (amount < 1) {
      toast.error(i18n.language === 'fr' ? 'Montant minimum: 1€' : 'Minimum amount: €1');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/donations/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          message,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Failed to create checkout');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la création du paiement' : 'Error creating payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            {afterMeetup 
              ? (i18n.language === 'fr' ? 'Merci pour votre participation !' : 'Thanks for participating!')
              : (i18n.language === 'fr' ? 'Soutenir XPerience' : 'Support XPerience')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {afterMeetup && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-medium">
                {i18n.language === 'fr' 
                  ? 'Vous êtes inscrit à cette rencontre !'
                  : 'You\'re registered for this meetup!'}
              </p>
            </div>
          )}

          <div className="text-center">
            <p className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'XPerience propose les rencontres gratuitement grâce aux dons de la communauté.'
                : 'XPerience offers meetups for free thanks to community donations.'}
            </p>
          </div>

          {/* Amount Selection */}
          <div>
            <label className="text-sm font-medium mb-3 block">
              {i18n.language === 'fr' ? 'Choisissez un montant' : 'Choose an amount'}
            </label>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {DONATION_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                  }}
                  className={`py-3 px-4 rounded-xl border-2 font-semibold transition-all ${
                    selectedAmount === amount && !customAmount
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {amount}€
                </button>
              ))}
            </div>
            
            {/* Custom Amount */}
            <div className="relative">
              <Input
                type="number"
                min="1"
                placeholder={i18n.language === 'fr' ? 'Autre montant' : 'Custom amount'}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {i18n.language === 'fr' ? 'Un petit mot ? (optionnel)' : 'A message? (optional)'}
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={i18n.language === 'fr' ? 'Merci pour cette super plateforme !' : 'Thanks for this great platform!'}
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1"
            >
              {i18n.language === 'fr' ? 'Plus tard' : 'Maybe later'}
            </Button>
            <Button 
              onClick={handleDonate}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Heart className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? `Donner ${getAmount()}€` : `Donate €${getAmount()}`}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            {i18n.language === 'fr' 
              ? 'Paiement sécurisé par Stripe. Merci de votre générosité !'
              : 'Secure payment by Stripe. Thank you for your generosity!'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small donation button component for use in various places
export const DonationButton = ({ variant = "outline", size = "sm" }) => {
  const { i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button 
        variant={variant} 
        size={size} 
        onClick={() => setShowModal(true)}
        className="gap-2"
      >
        <Heart className="w-4 h-4 text-pink-500" />
        {i18n.language === 'fr' ? 'Soutenir' : 'Support'}
      </Button>
      <DonationModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

// Success page after donation
export const DonationSuccessPage = () => {
  const { i18n } = useTranslation();
  
  return (
    <main className="min-h-screen pt-20 pb-16 flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-md mx-auto px-4 text-center"
      >
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-pink-100 to-rose-100 rounded-full flex items-center justify-center">
          <Heart className="w-10 h-10 text-pink-500" />
        </div>
        <h1 className="text-2xl font-bold mb-4">
          {i18n.language === 'fr' ? 'Merci infiniment !' : 'Thank you so much!'}
        </h1>
        <p className="text-muted-foreground mb-6">
          {i18n.language === 'fr' 
            ? 'Votre générosité permet à XPerience de rester gratuit pour les rencontres entre passionnés.'
            : 'Your generosity helps XPerience stay free for meetups between enthusiasts.'}
        </p>
        <Button onClick={() => window.location.href = '/explore'}>
          {i18n.language === 'fr' ? 'Continuer à explorer' : 'Continue exploring'}
        </Button>
      </motion.div>
    </main>
  );
};

export default DonationModal;
